import React, { useState } from 'react';
import { Alert, Badge, Group, Loader, Paper, Select, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useUsersDirectory } from '../../hooks/useUserApi';
import { ExplainSource, useExplain, useRolesCatalog } from '../../hooks/useRolesApi';

export const WhyTab: React.FC = () => {
  const { t } = useTranslation();
  const { data: catalog } = useRolesCatalog();
  const { data: people = [] } = useUsersDirectory();
  const [user, setUser] = useState<string | null>(null);
  const [permission, setPermission] = useState<string | null>(null);
  const { data, isFetching } = useExplain(user, permission);

  const permissionData = (catalog?.modules ?? []).map((m) => ({
    group: t(m.labelKey),
    items: catalog!.permissions
      .filter((p) => p.module === m.key)
      .map((p) => ({ value: p.key, label: t(p.labelKey) })),
  }));

  const titleOf = (s: ExplainSource) =>
    s.kind === 'owner'
      ? t('roles.why.owner')
      : s.kind === 'role'
        ? s.name
        : t(`roles.computed.${s.key}.name`);

  const detailOf = (s: ExplainSource): string => {
    if (s.kind === 'owner') return t('roles.why.ownerDetail');
    if (s.kind === 'role')
      return s.via
        .map((v) => t(v === 'flag' ? 'roles.why.viaFlag' : 'roles.why.viaAssignment'))
        .join('; ');
    if (!s.reasons?.length) return t(`roles.computed.${s.key}.who`);
    return s.reasons.map((r) => t(`roles.why.reason.${r.kind}`, { name: r.name })).join('; ');
  };

  return (
    <Stack gap="lg" maw={760}>
      <Group align="flex-end" gap="sm" wrap="wrap">
        <Select
          label={t('roles.people.person')}
          placeholder={t('roles.people.personPlaceholder')}
          data={people.map((p) => ({ value: p._id, label: p.name }))}
          value={user}
          onChange={setUser}
          searchable
          style={{ flex: '1 1 220px' }}
        />
        <Select
          label={t('roles.why.permission')}
          placeholder={t('roles.why.permissionPlaceholder')}
          data={permissionData}
          value={permission}
          onChange={setPermission}
          searchable
          style={{ flex: '1 1 260px' }}
        />
      </Group>

      {!user || !permission ? (
        <Text size="sm" c="dimmed">
          {t('roles.why.prompt')}
        </Text>
      ) : isFetching && !data ? (
        <Loader size="sm" />
      ) : data && !data.sources.length ? (
        <Alert variant="light" color="gray">
          {t('roles.why.none', { name: data.user.name })}
        </Alert>
      ) : (
        data && (
          <Stack gap="xs" aria-label={t('roles.why.sources')}>
            <Text size="sm" c="dimmed">
              {t('roles.why.has', {
                name: data.user.name,
                permission: t(`roles.permissions.${data.permission}`),
              })}
            </Text>
            {data.sources.map((s, i) => (
              <Paper key={i} withBorder radius="md" px="md" py="sm" data-source={s.kind}>
                <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
                  <div>
                    <Group gap={6}>
                      <Text size="sm" fw={600}>
                        {titleOf(s)}
                      </Text>
                      {s.kind === 'computed' && (
                        <Badge size="xs" variant="light" color="gray">
                          {t('roles.why.computed')}
                        </Badge>
                      )}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {detailOf(s)}
                    </Text>
                  </div>
                  <Badge variant="light" color="teal" style={{ flexShrink: 0 }}>
                    {t(`roles.scopes.${s.scope}`)}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Stack>
        )
      )}
    </Stack>
  );
};
